import { getProviderLabel } from "@frontend-shared/models/model-provider-label";
import type {
  IModel as IApiModel,
  IOrganizationModel,
  TOrganizationBaseModel,
} from "@frontend-shared/types";
import { Network } from "lucide-react";

import {
  Claude,
  DeepSeek,
  Glm,
  Google,
  Grok,
  KimiK,
  Mistral,
  OpenAI,
} from "@/components/icons";
import type { IModel } from "@/pages/chat/ModelPicker/types";

// NOTE: NSAI-5065 — this switch is the twin of the one in the other client
// (web <-> mobile). The provider -> icon decision is platform-agnostic and
// belongs in frontend-shared, but the components it returns do not: web pulls
// them from lucide-react and mobile from lucide-react-native. Until the
// decision is lifted out, both copies must be edited together; the pair is
// listed in sonar.cpd.exclusions with the same reasoning.
interface IGetModelIconParams {
  provider: string;
  hostingProvider?: string;
}

export const getModelIcon = ({
  provider,
  hostingProvider,
}: IGetModelIconParams) => {
  // NOTE: This is a temporary fix to display the NodeShift model icon
  if (hostingProvider?.toLowerCase() === "nodeshift") {
    return Network;
  }

  switch (provider.toLowerCase()) {
    case "openai":
      return OpenAI;
    case "claude":
      return Claude;
    // NOTE: NSAI-6259 — the coloured Google mark, not `GoogleColorless`. The
    // review asked for brand logos in their own colours, and every other vendor
    // here now renders in its own (Claude orange, DeepSeek blue, Mistral's
    // yellow-to-red ramp); OpenAI and Grok stay monochrome because their marks
    // genuinely are.
    case "gemini":
      return Google;
    case "mistral":
      return Mistral;
    case "grok":
      return Grok;
    case "deepseek":
      return DeepSeek;
    // NOTE: NSAI-5056 — Moonshot (Kimi) and Z.ai (GLM) used to be matched by a
    // `startsWith` on the model name, because their rows claimed provider
    // "openai". They carry their real vendor now, so they are ordinary cases.
    case "moonshot":
      return KimiK;
    case "zai":
      return Glm;
    default:
      return Network;
  }
};

export const getModelLabel = (model: Pick<IApiModel, "title" | "name">) => {
  return (
    model.title || model.name.charAt(0).toUpperCase() + model.name.slice(1)
  );
};

export const mapApiModelToUIModel = (apiModel: IApiModel): IModel => {
  return {
    ...apiModel,
    label: getModelLabel(apiModel),
    providerLabel: getProviderLabel(apiModel),
    icon: getModelIcon({
      provider: apiModel.provider,
      hostingProvider: apiModel.hostingProvider,
    }),
  };
};

export const mapApiOrganizationModelToUIModel = (
  apiModel: TOrganizationBaseModel,
): IOrganizationModel => {
  return {
    ...apiModel,
    label: getModelLabel(apiModel),
    providerLabel: getProviderLabel(apiModel),
    icon: getModelIcon({
      provider: apiModel.provider,
      hostingProvider: apiModel.hostingProvider,
    }),
  };
};

interface IGroupedModels {
  [region: string]: IModel[];
}

export const groupModelsByRegion = (models: IModel[]) => {
  const grouped: IGroupedModels = {};
  models.forEach((model) => {
    const region = model.region || "Global";
    if (!grouped[region]) grouped[region] = [];
    grouped[region].push(model);
  });
  return grouped;
};
